import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/**
 * IB-700 — Plan tab Calendar view. A standard month calendar grid (week starts Monday) with each
 * procurement's five key target dates plotted onto their day cells as chips. Props-driven and
 * self-contained (no self-fetch): it renders the SAME filtered item set as the Kanban.
 *
 * Props:
 *   - items: the flat procurement array (same shape the Kanban receives).
 */

// The five S/R/D/C/CC target-date keys → the short milestone label shown on the chip.
const MILESTONES = [
    { key: 'target_scope_issue_to_market_date', label: $t('Scope to market') },
    { key: 'target_response_due_date', label: $t('Response due') },
    { key: 'target_decision_date', label: $t('Decision') },
    { key: 'target_contract_award_date', label: $t('Award') },
    { key: 'target_contract_completion_date', label: $t('Completion') },
];

// Subtle per-milestone chip tints, anchored to the existing palette (no new tokens).
const MILESTONE_CHIP_CLASS = {
    target_scope_issue_to_market_date: 'tw-bg-charcoal-10 tw-text-charcoal-75',
    target_response_due_date: 'tw-bg-blue-10 tw-text-blue-75',
    target_decision_date: 'tw-bg-blue-15 tw-text-blue-75',
    target_contract_award_date: 'tw-bg-green-25 tw-text-charcoal-75',
    target_contract_completion_date: 'tw-bg-charcoal-15 tw-text-charcoal-75',
};

// Week starts Monday to match the design; abbreviations are translatable.
const WEEKDAYS = [$t('Mon'), $t('Tue'), $t('Wed'), $t('Thu'), $t('Fri'), $t('Sat'), $t('Sun')];

const MAX_EVENTS_PER_CELL = 3;

const PlanCalendar = ({ items }) => {
    const [monthCursor, setMonthCursor] = useState(moment().startOf('month'));

    const today = moment().format('YYYY-MM-DD');

    // Bucket every valid target date by YYYY-MM-DD once, for O(1) per-cell lookup.
    const eventsByDay = useMemo(() => {
        const buckets = {};
        (items ?? []).forEach((item) => {
            MILESTONES.forEach(({ key, label }) => {
                const raw = item[key];
                if (!raw) {
                    return;
                }
                // The resource serialises these `date`-cast fields as ISO datetimes
                // ("2025-07-22T00:00:00.000000Z"), so parse non-strict on the leading date (as the
                // Gantt does) — a strict 'YYYY-MM-DD' parse would reject every value.
                const day = moment(raw, 'YYYY-MM-DD');
                if (!day.isValid()) {
                    return;
                }
                const dayKey = day.format('YYYY-MM-DD');
                (buckets[dayKey] ??= []).push({ name: item.name, milestone: label, milestoneKey: key });
            });
        });
        return buckets;
    }, [items]);

    // Build the 7-wide grid: leading blanks (Mon=0), the month's day cells, trailing blanks.
    const cells = useMemo(() => {
        const first = monthCursor.clone().startOf('month');
        const startOffset = first.isoWeekday() - 1; // Mon=1 → 0-based offset.
        const daysInMonth = monthCursor.daysInMonth();

        const result = [];
        for (let i = 0; i < startOffset; i += 1) {
            result.push({ key: `lead-${i}`, blank: true });
        }
        for (let day = 1; day <= daysInMonth; day += 1) {
            const date = first.clone().date(day);
            const dateKey = date.format('YYYY-MM-DD');
            result.push({ key: dateKey, day, dateKey, events: eventsByDay[dateKey] ?? [] });
        }
        while (result.length % 7 !== 0) {
            result.push({ key: `trail-${result.length}`, blank: true });
        }
        return result;
    }, [monthCursor, eventsByDay]);

    const goPrev = () => setMonthCursor((cursor) => cursor.clone().subtract(1, 'month'));
    const goNext = () => setMonthCursor((cursor) => cursor.clone().add(1, 'month'));

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Month navigator */}
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
                <button
                    type="button"
                    onClick={goPrev}
                    aria-label={$t('Previous month')}
                    className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-charcoal-75 hover:tw-bg-charcoal-10 hover:tw-text-charcoal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue"
                >
                    <ChevronLeftIcon className="tw-h-3.5 tw-w-3.5" />
                </button>
                <span className="tw-min-w-[10rem] tw-text-center tw-text-base tw-font-semibold tw-text-charcoal">{monthCursor.format('MMMM YYYY')}</span>
                <button
                    type="button"
                    onClick={goNext}
                    aria-label={$t('Next month')}
                    className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-charcoal-75 hover:tw-bg-charcoal-10 hover:tw-text-charcoal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue"
                >
                    <ChevronRightIcon className="tw-h-3.5 tw-w-3.5" />
                </button>
            </div>

            {/* Weekday header */}
            <div className="tw-grid tw-grid-cols-7 tw-gap-1">
                {WEEKDAYS.map((weekday) => (
                    <div key={weekday} className="tw-py-1 tw-text-center tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                        {weekday}
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="tw-grid tw-grid-cols-7 tw-gap-1">
                {cells.map((cell, idx) => {
                    if (cell.blank) {
                        return <div key={cell.key} className="tw-min-h-[6rem] tw-rounded-md tw-bg-charcoal-10" />;
                    }

                    const isToday = cell.dateKey === today;
                    const extra = cell.events.length - MAX_EVENTS_PER_CELL;
                    const hasEvents = cell.events.length > 0;
                    // Anchor the hover panel to the right edge for the last two columns so it never
                    // overflows off the right of the calendar, and open it upward on the bottom two
                    // rows so it is never clipped below the grid.
                    const alignRight = idx % 7 >= 5;
                    const openUp = Math.floor(idx / 7) >= cells.length / 7 - 2;

                    return (
                        <div
                            key={cell.key}
                            className={classNames(
                                'tw-group tw-relative tw-flex tw-min-h-[6rem] tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-p-1.5 tw-transition-colors',
                                {
                                    'tw-border-blue tw-bg-blue-10': isToday,
                                    'tw-border-grey tw-bg-white': !isToday,
                                    'hover:tw-z-30 hover:tw-border-blue-25': hasEvents,
                                },
                            )}
                        >
                            <span className={classNames('tw-text-xs tw-font-semibold', isToday ? 'tw-text-blue' : 'tw-text-charcoal-75')}>{cell.day}</span>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                {cell.events.slice(0, MAX_EVENTS_PER_CELL).map((event, index) => (
                                    <span
                                        key={`${cell.dateKey}-${index}`}
                                        className={classNames(
                                            'tw-truncate tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[0.65rem] tw-font-medium',
                                            MILESTONE_CHIP_CLASS[event.milestoneKey],
                                        )}
                                    >
                                        {event.name} · {event.milestone}
                                    </span>
                                ))}
                                {extra > 0 && (
                                    <span className="tw-px-1 tw-text-[0.65rem] tw-font-medium tw-text-charcoal-50">
                                        {$t('+{{count}} more', { count: extra })}
                                    </span>
                                )}
                            </div>

                            {/* Whole-cell hover panel: the full, untruncated list of the day's events. */}
                            {hasEvents && (
                                <div
                                    className={classNames(
                                        'tw-invisible tw-absolute tw-z-20 tw-w-64 tw-max-w-[16rem] tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-3 tw-opacity-0 tw-shadow-primary tw-transition tw-duration-150 group-hover:tw-visible group-hover:tw-opacity-100',
                                        alignRight ? 'tw-right-0' : 'tw-left-0',
                                        openUp ? 'tw-bottom-full tw-mb-1' : 'tw-top-full tw-mt-1',
                                    )}
                                >
                                    <div className="tw-mb-2 tw-text-xs tw-font-semibold tw-text-charcoal">
                                        {moment(cell.dateKey, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
                                    </div>
                                    <div className="tw-flex tw-max-h-64 tw-flex-col tw-gap-1.5 tw-overflow-y-auto">
                                        {cell.events.map((event, index) => (
                                            <div key={`pop-${cell.dateKey}-${index}`} className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                                <span className="tw-text-xs tw-text-charcoal-75">{event.name}</span>
                                                <span
                                                    className={classNames(
                                                        'tw-shrink-0 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[0.6rem] tw-font-medium',
                                                        MILESTONE_CHIP_CLASS[event.milestoneKey],
                                                    )}
                                                >
                                                    {event.milestone}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PlanCalendar;
